import Image from 'next/image';
import { useRef, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { GmUserProfileInfo } from '@/components/features/gamification/user-profile/GmProfileInfo';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { AppRoutes } from '@/i18n/constants';
import { Link, usePathname } from '@/i18n/routing';
import avatar from '@/public/assets/image/profile/profile-avatar.webp';

interface UserProfileProps {
  showAttentionIcon?: boolean;
  onClickProfile?: () => void;
}

export const UserProfile = ({ showAttentionIcon, onClickProfile }: UserProfileProps) => {
  const pathname = usePathname();
  const isMobile = useMediaQuery('(max-width: 1024px)');

  const [isGmProfileInfoOpen, setIsGmProfileInfoOpen] = useState(false);
  const profileInfoRef = useRef<HTMLDivElement>(null);

  const isGamificationProfileInfoPage = pathname === AppRoutes.gamificationMissions || pathname === AppRoutes.gamificationRewards;
  const shouldOpenGamificationProfileInfo = isGamificationProfileInfoPage;

  useClickOutside(profileInfoRef, () => setIsGmProfileInfoOpen(false), {
    enabled: !isMobile && isGamificationProfileInfoPage && isGmProfileInfoOpen,
  });

  const handleOpenGamificationProfileInfo = () => {
    onClickProfile?.();
    setIsGmProfileInfoOpen(isOpen => !isOpen);
  };

  const avatarContent = (
    <div className="relative size-11 hover:scale-105 duration-300">
      <div className="size-11 overflow-hidden rounded-[6.25rem] bg-c900">
        <Image src={avatar} alt="avatar" className="size-11" width={88} height={88} />
      </div>
      {showAttentionIcon && (
        <div className="absolute bottom-0 -left-0.5 pr-0.5 -ml-px">
          <Icon name="attention" className="size-4 min-w-4 min-h-4 rounded-full shadow-[0_0_0_3px_#361D44]" />
        </div>
      )}
    </div>
  );

  return (
    <div ref={profileInfoRef} className="relative">
      {shouldOpenGamificationProfileInfo
        ? (
            <button
              type="button"
              aria-label="Open profile information"
              onClick={handleOpenGamificationProfileInfo}
              className="cursor-pointer transition-all duration-300"
            >
              {avatarContent}
            </button>
          )
        : (
            <Link
              href="/profile"
              prefetch={false}
              onClick={onClickProfile}
              className="cursor-pointer transition-all duration-300"
            >
              {avatarContent}
            </Link>
          )}

      {isGamificationProfileInfoPage && (
        <GmUserProfileInfo open={isGmProfileInfoOpen} onOpenChange={setIsGmProfileInfoOpen} />
      )}
    </div>

  );
};
